import React from "react";
import {
  FileText,
  Scale,
  Users,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Bike,
  Footprints,
  Heart,
  Mail,
  Ban,
  Copyright,
  Globe,
} from "lucide-react";
import { motion } from "framer-motion";
import PageLayout from "@/components/PageLayout";
import HeroSection from "@/components/HeroSection";
import Section from "@/components/Section";
import SectionHeader from "@/components/SectionHeader";
import { Button } from "@/components/Button";

// ============================================
// IMMAGINI
// ============================================

const HERO_IMAGE = "/images/99-castelli-header.webp";

// ============================================
// ANIMAZIONI
// ============================================

const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

// ============================================
// COMPONENTE
// ============================================

const TerminiServizio: React.FC = () => {
  const lastUpdate = "15 Gennaio 2026";

  return (
    <PageLayout>
      {/* Hero Section */}
      <HeroSection
        title="Termini e Condizioni"
        subtitle="Condizioni d'uso del sito e dei servizi"
        backgroundImage={HERO_IMAGE}
        height="medium"
      />

      {/* Intro */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-primary/5 rounded-xl p-6 border border-primary/20 mb-8"
          >
            <div className="flex items-center gap-3 mb-2">
              <Clock className="w-5 h-5 text-primary" />
              <span className="text-sm text-text-light">Ultimo aggiornamento: {lastUpdate}</span>
            </div>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="prose prose-lg max-w-none"
          >
            <p className="text-text-light leading-relaxed">
              I presenti Termini e Condizioni ("Termini") regolano l'accesso e l'utilizzo del sito web{" "}
              <strong className="text-foreground">www.99castelli.it</strong> ("Sito") e dei servizi offerti 
              da <strong className="text-foreground">ASD 99 Castelli</strong> ("Associazione").
            </p>
            <p className="text-text-light leading-relaxed">
              Utilizzando il Sito, l'utente dichiara di aver letto, compreso e accettato integralmente 
              i presenti Termini. Se non accetti questi Termini, ti preghiamo di non utilizzare il Sito.
            </p>
          </motion.div>
        </div>
      </Section>

      {/* Chi Siamo */}
      <Section background="gray">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-2xl font-display font-bold text-text">1. Chi Siamo</h2>
            </div>
            
            <div className="space-y-4 text-text-light">
              <p>
                <strong className="text-foreground">ASD 99 Castelli</strong> è un'Associazione Sportiva 
                Dilettantistica senza scopo di lucro, con sede in San Marco di Preturo, L'Aquila (AQ), 
                affiliata al <strong className="text-foreground">CSI - Centro Sportivo Italiano</strong>, 
                Comitato di L'Aquila.
              </p>
              <p>
                L'Associazione ha come finalità la promozione della scoperta dei castelli medievali 
                del territorio aquilano attraverso attività cicloturistiche, escursioni a piedi, 
                eventi culturali e manifestazioni sportive.
              </p>
              <div className="bg-muted rounded-lg p-4 mt-4">
                <p className="text-sm">
                  <strong>Codice Fiscale:</strong> 93116590667<br />
                  <strong>Codice ASD:</strong> 06700181<br />
                  <strong>IBAN:</strong> IT74T0832740700000000008756
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* Servizi Offerti */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            title="2. Servizi Offerti"
            subtitle="Cosa offre l'Associazione"
          />

          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border"
          >
            <p className="text-text-light mb-6">
              Il Sito fornisce informazioni sui servizi e le attività dell'Associazione, che includono:
            </p>
            
            <div className="grid md:grid-cols-2 gap-4 mb-6">
              <div className="flex items-start gap-3">
                <Bike className="w-6 h-6 text-primary flex-shrink-0" />
                <div>
                  <strong className="text-foreground">Uscite cicloturistiche</strong>
                  <p className="text-sm text-text-light">Escursioni in bicicletta alla scoperta dei castelli</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Footprints className="w-6 h-6 text-primary flex-shrink-0" />
                <div>
                  <strong className="text-foreground">Camminate</strong>
                  <p className="text-sm text-text-light">Passeggiate ed escursioni a piedi</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Users className="w-6 h-6 text-primary flex-shrink-0" />
                <div>
                  <strong className="text-foreground">Eventi culturali</strong>
                  <p className="text-sm text-text-light">Manifestazioni e momenti di condivisione</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Heart className="w-6 h-6 text-primary flex-shrink-0" />
                <div>
                  <strong className="text-foreground">Tesseramento</strong>
                  <p className="text-sm text-text-light">Iscrizione all'Associazione con copertura assicurativa</p>
                </div>
              </div>
            </div>

            <p className="text-text-light">
              La partecipazione alle attività dell'Associazione è riservata ai soci regolarmente tesserati.
            </p>
          </motion.div>
        </div>
      </Section>

      {/* Tesseramento */}
      <Section background="gray">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            title="3. Tesseramento e Partecipazione"
            subtitle="Requisiti per partecipare alle attività"
          />

          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border mb-6"
          >
            <div className="flex items-center gap-3 mb-6">
              <Shield className="w-6 h-6 text-primary" />
              <h3 className="text-lg font-semibold text-text">Requisiti per il tesseramento</h3>
            </div>
            
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-text-light">Compilazione e firma del modulo di iscrizione</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-text-light">Versamento della quota associativa annuale</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-text-light">Invio di copia del documento di identità</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-text-light">Certificato medico (ove previsto per l'attività scelta)</span>
              </li>
              <li className="flex items-start gap-3">
                <CheckCircle className="w-5 h-5 text-green-500 mt-0.5 flex-shrink-0" />
                <span className="text-text-light">Accettazione dello Statuto e dell'Informativa Privacy</span>
              </li>
            </ul>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-amber-50 rounded-xl p-6 border border-amber-200"
          >
            <div className="flex items-center gap-3 mb-2">
              <AlertTriangle className="w-6 h-6 text-amber-600" />
              <h3 className="text-lg font-semibold text-amber-800">Importante</h3>
            </div>
            <p className="text-amber-700">
              Il tesseramento ha validità annuale (1 gennaio - 31 dicembre) e deve essere rinnovato ogni anno.
              Senza tesseramento valido non è possibile partecipare alle attività dell'Associazione.
            </p>
          </motion.div>
        </div>
      </Section>

      {/* Responsabilità */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            title="4. Limitazioni di Responsabilità"
            subtitle="Esclusioni e limitazioni"
          />

          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-red-50 rounded-xl p-8 border border-red-200 mb-6"
          >
            <div className="flex items-center gap-3 mb-4">
              <Scale className="w-6 h-6 text-red-600" />
              <h3 className="text-lg font-bold text-red-800">Esclusione di responsabilità</h3>
            </div>
            <p className="text-red-700 font-medium">
              La ASD 99 Castelli non è in alcun modo responsabile dal punto di vista penale e civile 
              degli eventuali danni causati dalla pratica delle attività statutarie.
            </p>
          </motion.div>

          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border"
          >
            <p className="text-text-light mb-4">
              Inoltre, l'Associazione non può essere ritenuta responsabile per:
            </p>
            <ul className="space-y-3">
              <li className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-text-light">Eventuali errori o inesattezze presenti nei contenuti del Sito</span>
              </li>
              <li className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-text-light">Danni derivanti dall'uso o dall'impossibilità di usare il Sito</span>
              </li>
              <li className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-text-light">Contenuti di siti terzi raggiungibili tramite link esterni</span>
              </li>
              <li className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-text-light">Interruzioni o malfunzionamenti del Sito</span>
              </li>
              <li className="flex items-start gap-3">
                <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                <span className="text-text-light">Infortuni occorsi durante le attività a persone non regolarmente tesserate</span>
              </li>
            </ul>
          </motion.div>
        </div>
      </Section>

      {/* Comportamento Utente */}
      <Section background="gray">
        <div className="max-w-4xl mx-auto">
          <SectionHeader
            title="5. Obblighi dell'Utente"
            subtitle="Comportamenti richiesti e vietati"
          />

          <div className="grid md:grid-cols-2 gap-6">
            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="bg-green-50 rounded-xl p-6 border border-green-200"
            >
              <div className="flex items-center gap-3 mb-4">
                <CheckCircle className="w-6 h-6 text-green-600" />
                <h3 className="text-lg font-semibold text-green-800">L'utente si impegna a:</h3>
              </div>
              <ul className="space-y-2 text-green-700">
                <li>• Utilizzare il Sito in modo lecito e corretto</li>
                <li>• Fornire informazioni veritiere e accurate</li>
                <li>• Rispettare lo Statuto dell'Associazione</li>
                <li>• Mantenere riservate le credenziali di accesso</li>
                <li>• Rispettare gli altri partecipanti alle attività</li>
              </ul>
            </motion.div>

            <motion.div
              variants={fadeInUp}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="bg-red-50 rounded-xl p-6 border border-red-200"
            >
              <div className="flex items-center gap-3 mb-4">
                <Ban className="w-6 h-6 text-red-600" />
                <h3 className="text-lg font-semibold text-red-800">È vietato:</h3>
              </div>
              <ul className="space-y-2 text-red-700">
                <li>• Utilizzare il Sito per scopi illeciti</li>
                <li>• Diffondere contenuti offensivi o diffamatori</li>
                <li>• Violare diritti di proprietà intellettuale</li>
                <li>• Tentare di accedere a aree riservate</li>
                <li>• Compromettere la sicurezza del Sito</li>
              </ul>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* Proprietà Intellettuale */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border"
          >
            <div className="flex items-center gap-3 mb-6">
              <Copyright className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-display font-bold text-text">6. Proprietà Intellettuale</h2>
            </div>
            
            <div className="space-y-4 text-text-light">
              <p>
                Tutti i contenuti presenti sul Sito (testi, immagini, loghi, grafica, video) sono di 
                proprietà esclusiva di <strong className="text-foreground">ASD 99 Castelli</strong> o 
                utilizzati su licenza, e sono protetti dalle leggi italiane e internazionali sul 
                diritto d'autore e sulla proprietà intellettuale.
              </p>
              <p>
                È vietata qualsiasi riproduzione, distribuzione, modifica o utilizzo dei contenuti 
                senza preventiva autorizzazione scritta dell'Associazione.
              </p>
              <p>
                Il marchio "99 Castelli" e il relativo logo sono marchi registrati. L'uso non 
                autorizzato è severamente vietato.
              </p>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* Link Esterni */}
      <Section background="gray">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border"
          >
            <div className="flex items-center gap-3 mb-6">
              <Globe className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-display font-bold text-text">7. Link a Siti Esterni</h2>
            </div>
            
            <p className="text-text-light">
              Il Sito può contenere link a siti web di terze parti (es. CSI, Facebook, Instagram). 
              L'Associazione non ha alcun controllo su tali siti e non è responsabile dei loro 
              contenuti, delle loro politiche sulla privacy o delle loro pratiche. L'inclusione 
              di link non implica approvazione o affiliazione con tali siti.
            </p>
          </motion.div>
        </div>
      </Section>

      {/* Modifiche */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border"
          >
            <div className="flex items-center gap-3 mb-6">
              <FileText className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-display font-bold text-text">8. Modifiche ai Termini</h2>
            </div>
            
            <p className="text-text-light">
              L'Associazione si riserva il diritto di modificare i presenti Termini in qualsiasi momento. 
              Le modifiche saranno efficaci dalla data di pubblicazione sul Sito. L'uso continuato del 
              Sito dopo la pubblicazione delle modifiche costituisce accettazione dei nuovi Termini.
              Si consiglia di consultare periodicamente questa pagina.
            </p>
          </motion.div>
        </div>
      </Section>

      {/* Legge Applicabile */}
      <Section background="gray">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-surface rounded-xl p-8 shadow-card border border-border"
          >
            <div className="flex items-center gap-3 mb-6">
              <Scale className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-display font-bold text-text">9. Legge Applicabile e Foro Competente</h2>
            </div>
            
            <p className="text-text-light">
              I presenti Termini sono regolati dalla legge italiana. Per qualsiasi controversia 
              relativa all'interpretazione, esecuzione o risoluzione dei presenti Termini sarà 
              competente in via esclusiva il Foro di <strong className="text-foreground">L'Aquila</strong>.
            </p>
          </motion.div>
        </div>
      </Section>

      {/* Contatti */}
      <Section background="white">
        <div className="max-w-4xl mx-auto">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="bg-primary/5 rounded-xl p-8 border border-primary/20"
          >
            <div className="flex items-center gap-3 mb-6">
              <Mail className="w-6 h-6 text-primary" />
              <h2 className="text-2xl font-display font-bold text-text">10. Contatti</h2>
            </div>
            
            <p className="text-text-light mb-4">
              Per qualsiasi domanda o chiarimento sui presenti Termini, puoi contattarci:
            </p>
            
            <div className="space-y-2 text-text-light">
              <p><strong className="text-foreground">Email:</strong> <a href="mailto:99castelli@gmail.com" className="text-primary hover:underline">99castelli@gmail.com</a></p>
              <p><strong className="text-foreground">Telefono:</strong> <a href="tel:+393402894567" className="text-primary hover:underline">340 2894567</a></p>
              <p><strong className="text-foreground">Sede:</strong> San Marco di Preturo, L'Aquila (AQ)</p>
            </div>
          </motion.div>
        </div>
      </Section>

      {/* CTA */}
      <Section background="primary" className="text-center">
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="max-w-2xl mx-auto"
        >
          <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
            Pronto a unirti a noi?
          </h2>
          <p className="text-lg text-white/80 mb-8">
            Accettando questi Termini, potrai diventare socio e partecipare alle nostre avventure!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              variant="secondary"
              size="lg"
              onClick={() => window.location.href = "/tesseramento"}
            >
              Diventa Socio
            </Button>
            <Button
              variant="outline"
              size="lg"
              onClick={() => window.location.href = "/privacy"}
              className="border-white text-white hover:bg-white hover:text-primary"
            >
              Privacy Policy
            </Button>
          </div>
        </motion.div>
      </Section>
    </PageLayout>
  );
};

export default TerminiServizio;
